import { Button } from '@design-system/button';
import { Icon } from '@design-system/icon';
import { Link } from '@design-system/link';
import classNames from 'classnames';
import React, { FC } from 'react';

import ExtendedGlobalSubmenu from '~/components/ExtendedHeaderSubmenu/ExtendedHeaderSubmenu';

import { useDropdown } from '../../hooks/useDropdown';
import { getLinkIcon } from '../../utils/getLinkIcon';
import { getLinkSize } from '../../utils/getLinkSize';
import { isActive } from '../../utils/isActive';
import { NavigationBasicItemProps } from './models';

export const NavigationBasicItem: FC<NavigationBasicItemProps> = ({
  navigationData,
  activeKey,
  onHoverOpen,
  onHoverSoftClose,
  className,
  linkNavElement,
  activeUrlPath,
  href = '',
  ariaLabel,
  isMobileView,
  label,
  subItems,
  openIcon,
  closeIcon,
  expandTitle,
}) => {
  const LinkElement = linkNavElement || Link;
  const { handleDesktopOutsideBlur, handleKeyPress, isOpen, handleMouseOver } =
    useDropdown();

  const hasSubItems = Boolean(subItems?.length);
  const onMouseOverDesktop =
    hasSubItems && !isMobileView ? handleMouseOver : undefined;

  const handleToggle = () => {
    if (activeKey === label) onHoverSoftClose?.();
    else onHoverOpen?.();
  };

  const liHandlers: React.HTMLAttributes<HTMLLIElement> = isMobileView
    ? { onClick: handleToggle }
    : {
        onMouseEnter: () => onHoverOpen?.(),
        onMouseLeave: () => onHoverSoftClose?.(),
      };

  const openCloseIcon = (
    <Icon
      color="#C41974"
      icon={activeKey && activeKey === label ? 'arrowDown' : 'arrowUp'}
    />
  );

  return (
    <li
      {...liHandlers}
      className={classNames('cds-global-header__navigation-item', className, {
        'cds-global-header__navigation-item--has-children': hasSubItems,
        active: href && isActive(activeUrlPath, href, subItems || []),
      })}
      onBlur={handleDesktopOutsideBlur}
    >
      {href ? (
        <>
          <LinkElement
            className={classNames('cds-global-header__navigation-link', {
              active: isActive(
                decodeURIComponent(activeUrlPath),
                href,
                subItems || []
              ),
              'has-submenu': hasSubItems,
            })}
            onClick={handleClick}
            aria-current={decodeURIComponent(activeUrlPath) === href}
            onMouseOver={onMouseOverDesktop}
            href={href}
            variant="nav"
            size={getLinkSize(isMobileView)}
            aria-label={ariaLabel}
          >
            {label}
          </LinkElement>
          {hasSubItems && (
            <Button
              variant={isMobileView ? 'primary-icon' : 'nav'}
              onClick={handleClick}
              onKeyDown={handleKeyPress}
              onMouseOver={onMouseOverDesktop}
              size="small"
              className="cds-global-header__navigation-arrow"
              aria-expanded={isOpen}
              iconSuffix={getLinkIcon(openIcon, closeIcon, isOpen)}
            >
              {`${expandTitle} ${label}`}
            </Button>
          )}
        </>
      ) : (
        <Button
          className={classNames('cds-global-header__navigation-btn', {
            'has-submenu': hasSubItems,
          })}
          variant="nav"
          onKeyDown={handleKeyPress}
          onMouseOver={onMouseOverDesktop}
          size={getLinkSize(isMobileView)}
          aria-label={ariaLabel}
          aria-expanded={isOpen}
          iconSuffix={
            hasSubItems ? getLinkIcon(openIcon, closeIcon, isOpen) : undefined
          }
        >
          <div className="cds-global-header__label-with-icon">
            {label}
            {isMobileView && openCloseIcon}
          </div>
        </Button>
      )}
      {isMobileView && activeKey === label && (
        <ExtendedGlobalSubmenu
          navigation={navigationData}
          activeKey={activeKey || ''}
        />
      )}
    </li>
  );
};
